/**
 * Report purchase API client — thin fetch wrappers over the Worker routes
 * in src/api/routes/purchases.ts.
 *
 * The checkout call returns either a provider checkout URL (Lemon Squeezy
 * hosted page — redirect the browser there) or, in dev/QA mode, a ready
 * report URL. The report endpoint is polled by the report page because the
 * buyer can land on /report/:token from the provider redirect a few seconds
 * BEFORE the webhook flips the purchase to `paid`.
 */

const API_BASE = import.meta.env.VITE_API_URL ?? '';

export interface CheckoutResponse {
  checkout_url?: string;
  report_url?: string;
  token?: string;
  dev?: boolean;
  error?: string;
}

export interface ReportAccessResponse {
  status: 'pending' | 'paid' | 'refunded';
  product: string;
  lang: 'en' | 'ru';
  /** encodeAnswersForUrl() blob; null until the purchase is paid. */
  answers: string | null;
  paid_at: string | null;
  error?: string;
}

export async function createReportCheckout(args: {
  answers: string;
  lang: 'en' | 'ru';
  email?: string;
}): Promise<CheckoutResponse> {
  const res = await fetch(`${API_BASE}/api/purchases/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(args),
  });
  const data = (await res.json().catch(() => ({}))) as CheckoutResponse;
  if (!res.ok) {
    throw new Error(data.error ?? `HTTP ${res.status}`);
  }
  return data;
}

export async function fetchReportAccess(token: string): Promise<ReportAccessResponse> {
  const res = await fetch(`${API_BASE}/api/report/${encodeURIComponent(token)}`);
  const data = (await res.json().catch(() => ({}))) as ReportAccessResponse;
  if (!res.ok) {
    throw new Error(data.error ?? `HTTP ${res.status}`);
  }
  return data;
}
